import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import { ClientSearchFiltersCheckboxLabel } from "@src/multiCustodian/components/Advisor/Clients/types";
import { usePersistedQueryParams } from "@src/multiCustodian/hooks/Advisor/usePersistedQueryParams";
import { CheckboxListConfig } from "@src/sharedComponents/ScrollableCheckboxList/ScrollableCheckboxList";

import { getFiltersFromTab, saveFiltersToTab } from "./storage";

// Define default client filters
/* eslint-disable import/no-unused-modules */
export const CLIENT_FILTERS_DEFAULT: CheckboxListConfig<ClientSearchFiltersCheckboxLabel> =
  {
    Name: {
      isParent: false,
      checked: true,
      hasTopBorder: false,
      children: null,
    },
    Household: {
      isParent: false,
      checked: true,
      hasTopBorder: false,
      children: null,
    },
    Email: {
      isParent: false,
      checked: false,
      hasTopBorder: false,
      children: null,
    },
    Mobile: {
      isParent: false,
      checked: false,
      hasTopBorder: false,
      children: null,
    },
    "Client ID": {
      isParent: false,
      checked: false,
      hasTopBorder: false,
      children: null,
    },
    Advisor: {
      isParent: false,
      checked: false,
      hasTopBorder: false,
      children: null,
    },
    Created: {
      isParent: false,
      checked: false,
      hasTopBorder: false,
      children: null,
    },
  };

// Interface for URL query parameters
type ClientQueryParams = Record<string, string | undefined>;

interface ClientsFiltersContextProps {
  search: string;
  searchFilters: CheckboxListConfig<ClientSearchFiltersCheckboxLabel>;
  setSearch: (search: string) => void;
  setSearchFilters: (
    filters: CheckboxListConfig<ClientSearchFiltersCheckboxLabel>
  ) => void;
  resetFilters: () => void;
}

const ClientsFiltersContext = createContext<
  ClientsFiltersContextProps | undefined
>(undefined);

interface ClientsFiltersProviderProps {
  searchQueryParam?: string;
  children: React.ReactNode;
}

export const ClientsFiltersProvider: React.FC<ClientsFiltersProviderProps> = ({
  children,
  searchQueryParam = "clientQuery",
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Ref to track if we've restored from session storage yet
  const hasRestoredFromStorage = React.useRef(false);

  // Get initial URL state from storage once on mount
  const savedUrlFromStorage = React.useRef<string | undefined>(undefined);

  // Create ref for storing filters first to avoid circular reference
  const savedFiltersRef = React.useRef<
    CheckboxListConfig<ClientSearchFiltersCheckboxLabel>
  >(CLIENT_FILTERS_DEFAULT);

  // Get all saved filters from session storage - load only once and store in refs
  React.useMemo(() => {
    // Only read from storage once on initial render
    if (!hasRestoredFromStorage.current) {
      const filters = getFiltersFromTab({
        searchFilters: CLIENT_FILTERS_DEFAULT,
      });

      // Store the URL in our ref for later use
      savedUrlFromStorage.current = filters.url;

      // Store the filters in our ref
      if (filters.searchFilters) {
        savedFiltersRef.current = filters.searchFilters;
      }

      hasRestoredFromStorage.current = true;
    }
  }, []);

  // Determine initial URL parameters
  // If we navigate directly to the page with URL params, use those
  // Otherwise, restore from session storage
  const initialParams = React.useMemo<ClientQueryParams>(() => {
    // If URL already has search params, use those (direct navigation with params)
    if (location.search) {
      console.log("Using existing URL params:", location.search);
      return {}; // Let usePersistedQueryParams handle the current URL
    }

    // If we have saved URL params in storage, use those
    if (savedUrlFromStorage.current) {
      try {
        console.log("Restoring URL from storage:", savedUrlFromStorage.current);
        const searchParams = new URLSearchParams(savedUrlFromStorage.current);

        // Extract all params into an object
        const params: Record<string, string> = {};
        searchParams.forEach((value, key) => {
          params[key] = value;
        });

        return params as ClientQueryParams;
      } catch (error) {
        console.error("Error parsing saved URL:", error);
      }
    }

    // Default to empty search query param if nothing else
    return { [searchQueryParam]: "" };
  }, [location.search, searchQueryParam]);

  // Set up persisted query params for URL
  const [queryParams, setQueryParams] =
    usePersistedQueryParams<ClientQueryParams>(
      initialParams,
      {
        parseBooleans: true,
        decode: true,
        arrayFormat: "bracket-separator",
      },
      {
        encode: true,
        arrayFormat: "bracket-separator",
      }
    );

  // Set up state for search filters
  const [searchFilters, setSearchFiltersState] = useState<
    CheckboxListConfig<ClientSearchFiltersCheckboxLabel>
  >(savedFiltersRef.current);

  // Derived search value from URL query param
  const search = useMemo(
    () => queryParams[searchQueryParam] || "",
    [queryParams, searchQueryParam]
  );

  // Flag to track if we've completed initial setup
  const initialSetupComplete = React.useRef(false);

  // Only save to storage after initial setup is complete
  // This prevents overwriting storage during restoration phase
  React.useEffect(() => {
    const timer = setTimeout(() => {
      initialSetupComplete.current = true;
      console.log("Initial setup complete, will save changes to storage now");
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  // Save filter settings and URL to session storage
  useEffect(() => {
    // Skip saving during initial setup to prevent overwriting our just-loaded values
    if (!initialSetupComplete.current) {
      return;
    }

    // Only save if we have real URL params
    if (location.search) {
      console.log("Saving to storage, URL:", location.search);
      saveFiltersToTab({
        searchFilters: searchFilters,
        url: location.search,
      });
    }
  }, [searchFilters, location.search]);

  // Save state before navigating away
  useEffect(() => {
    // Function to capture current URL state before unload/navigation
    const saveUrlBeforeNavigation = () => {
      // Only save if we have a non-empty URL and setup is complete
      if (location.search && initialSetupComplete.current) {
        console.log("Navigation detected, saving URL:", location.search);
        saveFiltersToTab({
          searchFilters: searchFilters,
          url: location.search,
        });
      }
    };

    // Add event listener for when user navigates away
    window.addEventListener("beforeunload", saveUrlBeforeNavigation);

    // Clean up event listener
    return () => {
      // This also gets called when component unmounts (navigating away from page)
      saveUrlBeforeNavigation();
      window.removeEventListener("beforeunload", saveUrlBeforeNavigation);
    };
  }, [location.search, searchFilters]);

  // Update search in URL and session storage
  const setSearch = (newSearch: string) => {
    // Keep empty strings in the URL to allow for proper clearing of the field
    // but still handle undefined values
    const updatedParams = {
      ...queryParams,
      [searchQueryParam]: newSearch !== undefined ? newSearch : "",
    };
    setQueryParams(updatedParams);
  };

  // Update search filters in state and session storage
  const setSearchFilters = (
    newFilters: CheckboxListConfig<ClientSearchFiltersCheckboxLabel>
  ) => {
    setSearchFiltersState(newFilters);
  };

  // Reset all filters to default
  const resetFilters = () => {
    setSearchFiltersState(CLIENT_FILTERS_DEFAULT);
    setQueryParams({ [searchQueryParam]: "" }); // Set to empty string instead of undefined

    // Clear all URL params
    navigate({
      ...location,
      search: "",
    });
  };

  return (
    <ClientsFiltersContext.Provider
      value={{
        search,
        searchFilters,
        setSearch,
        setSearchFilters,
        resetFilters,
      }}
    >
      {children}
    </ClientsFiltersContext.Provider>
  );
};

export const useClientsFilters = () => {
  const context = useContext(ClientsFiltersContext);

  if (!context) {
    throw new Error(
      "useClientsFilters must be used within ClientsFiltersContext"
    );
  }

  return context;
};
